// hook_au/useAuth.tsx
import { createContext, useContext, useMemo, useCallback, useState, useEffect, type ReactNode } from 'react';
import apiClient, { setAccessToken } from '../services/apiClient';
import { registerAuthEvents } from './authEvents';
import type { UserRole } from '../types/auth';

const TOKEN_STORAGE_KEY = 'interiorlabs-token';
const USER_STORAGE_KEY = 'interiorlabs-user';
type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

export interface CurrentUser {
  id: string;
  name: string;
  apellidos?: string;
  email?: string;
  role: UserRole;
}

type AuthContextValue = {
  token: string | null;
  user: CurrentUser | null;
  role: UserRole | null;
  authStatus: AuthStatus;
  isAuthenticated: boolean;
  setSession: (session: { token: string; user: CurrentUser }) => void;
  clearSession: () => void;
  refreshCurrentUser: () => Promise<CurrentUser | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_STORAGE_KEY));
  const [user, setUser] = useState<CurrentUser | null>(() => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!storedToken) {
      localStorage.removeItem(USER_STORAGE_KEY);
      return null;
    }
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as CurrentUser) : null;
  });
  const [authStatus, setAuthStatus] = useState<AuthStatus>(() =>
    localStorage.getItem(TOKEN_STORAGE_KEY) ? 'checking' : 'anonymous',
  );

  const setSession = useCallback(({ token: nextToken, user: nextUser }: { token: string; user: CurrentUser }) => {
    setToken(nextToken);
    setUser(nextUser);
    setAuthStatus('authenticated');
    setAccessToken(nextToken);
    localStorage.setItem(TOKEN_STORAGE_KEY, nextToken);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
  }, []);

  const clearSession = useCallback(() => {
    setToken(null);
    setUser(null);
    setAuthStatus('anonymous');
    setAccessToken(null);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  }, []);

  const refreshCurrentUser = useCallback(async () => {
    if (!token) {
      setAuthStatus('anonymous');
      return null;
    }
    try {
      const response = await apiClient.get<CurrentUser>('/me');
      setUser(response.data);
      setAuthStatus('authenticated');
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(response.data));
      return response.data;
    } catch {
      clearSession();
      return null;
    }
  }, [token, clearSession]);

  useEffect(() => {
    if (token && authStatus === 'checking') {
      void refreshCurrentUser();
    }
  }, [token, authStatus, refreshCurrentUser]);

  useEffect(() => {
    registerAuthEvents({
      onTokenUpdate: (nextToken) => {
        setToken(nextToken);
        setAccessToken(nextToken);
        if (nextToken) {
          localStorage.setItem(TOKEN_STORAGE_KEY, nextToken);
          setAuthStatus((currentStatus) =>
            currentStatus === 'authenticated' ? currentStatus : 'checking',
          );
        } else {
          setUser(null);
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(USER_STORAGE_KEY);
          setAuthStatus('anonymous');
        }
      },
      onClearSession: () => {
        clearSession();
      },
    });
  }, [clearSession]);

  useEffect(() => {
    setAccessToken(token);
  }, [token]);

  const value = useMemo(
    () => ({
      token,
      user,
      role: user?.role ?? null,
      authStatus,
      isAuthenticated: authStatus === 'authenticated',
      setSession,
      clearSession,
      refreshCurrentUser,
    }),
    [token, user, authStatus, setSession, clearSession, refreshCurrentUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
